/*
 * Copyright (c) 2025. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import type {
  CableLengths,
  ComponentId,
  ConfigComponentArchivedSummary,
  ConfigQueryResults,
  ConfigurationWithoutRequest,
  FlowRunLogs,
  HVCostDatabase,
  HVCostDatabaseV4,
  ListResultsQuery,
  MetSoilRowWithMetStation,
  OptionalCompoundId,
  PFDesignResults,
  PVBillOfMaterials,
  PVCostDatabase,
  PVCostingConfiguration,
  PVCostingOutputs,
  PVCostingRequest,
  PVCostingRequestWithName,
  PVSimConfiguration,
  PVSimulationRequest,
  PVSimulationRequestWithName,
  PvCostingResult,
  ScenarioRecord,
  ScenarioWorkflow,
  Task,
} from '@poweralpha/common';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { minutesToMilliseconds } from 'date-fns';
import type { HTTPError } from 'ky';
import {
  api,
  getMultipleResults,
  getQueryConfigsSearchParams,
  getResult,
  type QueryConfigsSearchParams,
} from '../api';
import { objectToQueryKey } from '../queryClient';
import type { ConfigType } from '../react-components/configs/models';
import type { MutationOptions, QueryOptions } from '../react-components/queries';
import type {
  AcCableQuantitiesRequest,
  CostingVariables,
  PrevResultsComparisonQuery,
  RefreshBomParams,
} from './pvcosting/models';
import type { HorizonProfileQueryResult } from './pvsim/models';

export const usePvSimConfigMetaQuery = (
  ids: Array<[string, number]>,
  opts?: QueryOptions<ListResultsQuery<ConfigurationWithoutRequest>, HTTPError>,
) => {
  const stringifiedIds = ids.map(([id, version]) => `${id}_${version}`);
  return useQuery<ListResultsQuery<ConfigurationWithoutRequest>, HTTPError>({
    queryKey: ['pvsim-config-meta', ...ids],
    queryFn: () => api.get(`pvsim/multiple-configs?id=${stringifiedIds.join('&id=')}`).json(),
    ...opts,
    enabled: ids.length ? opts?.enabled : false,
  });
};

export const usePvConfigMetaQuery = (
  ids: Array<[string, number]>,
  opts?: QueryOptions<ListResultsQuery<ConfigurationWithoutRequest>, HTTPError>,
) => {
  const stringifiedIds = ids.map(([id, version]) => `${id}_${version}`);
  return useQuery<ListResultsQuery<ConfigurationWithoutRequest>, HTTPError>({
    queryKey: ['pv-config-meta', ...ids],
    queryFn: () =>
      api.get(`pvsim/costing/multiple-configs?id=${stringifiedIds.join('&id=')}`).json(),
    ...opts,
    enabled: ids.length ? opts?.enabled : false,
  });
};

export const getPvsimConfigurations = async <T = PVSimConfiguration>(
  queryOptions?: QueryConfigsSearchParams,
): Promise<ConfigQueryResults<T>> => {
  const searchParams = getQueryConfigsSearchParams(queryOptions);
  return api.get('pvsim/configs', { searchParams }).json();
};

export const getPvsimConfiguration = async (
  id: string | undefined | null,
  version?: number,
): Promise<PVSimConfiguration> =>
  api
    .get(`pvsim/configs/${id}`, version !== undefined ? { searchParams: { version } } : {})
    .json<PVSimConfiguration>();

export const getPvsimConfigMaxVersion = async (id: string | undefined | null): Promise<number> =>
  api.get(`pvsim/configs/${id}/max-version`).json();

export const savePvsimConfiguration = async (
  request: PVSimulationRequestWithName,
): Promise<PVSimConfiguration> => api.post('pvsim/configs', { json: request }).json();

export const editPvsimConfiguration = async (
  request: PVSimulationRequestWithName,
  id: string | null,
  version: number,
): Promise<PVSimConfiguration> =>
  api.post(`pvsim/configs/${id}/${version}`, { json: request }).json();

export const archivePvsimConfiguration = async (id: string | undefined): Promise<void> =>
  api.post(`pvsim/configs/${id}/archive`).json();

export const getHorizonProfile = async (
  latitude: number,
  longitude: number,
): Promise<HorizonProfileQueryResult> =>
  api.get('pvsim/horizon-profile', { searchParams: { latitude, longitude } }).json();

export const runPVSimulation = async (
  request: PVSimulationRequest,
  configId: [string, number] | null,
): Promise<Task> =>
  api
    .post('pvsim/simulate', { searchParams: { report: 'true' }, json: { request, configId } })
    .json();

export const getPvCostingConfigurations = async <T = PVCostingConfiguration>(
  queryOptions?: QueryConfigsSearchParams,
): Promise<ConfigQueryResults<T>> => {
  const searchParams = getQueryConfigsSearchParams(queryOptions);
  return api.get('pvsim/costing/configs', { searchParams }).json();
};

export const getPvCostingConfiguration = async (
  id: string | undefined | null,
  version?: number,
): Promise<PVCostingConfiguration> =>
  api
    .get(`pvsim/costing/configs/${id}`, version !== undefined ? { searchParams: { version } } : {})
    .json<PVCostingConfiguration>();

export const getPvCostingConfigMaxVersion = async (
  id: string | undefined | null,
): Promise<number> => api.get(`pvsim/costing/configs/${id}/max-version`).json();

export const savePvCostingConfiguration = async (
  request: PVCostingRequestWithName,
): Promise<PVCostingConfiguration> => api.post('pvsim/costing/configs', { json: request }).json();

export const editPvCostingConfiguration = async (
  request: PVCostingRequestWithName,
  id: string | null,
  version: number,
): Promise<PVCostingConfiguration> =>
  api.post(`pvsim/costing/configs/${id}/${version}`, { json: request }).json();

export const getArchivedPvCostingComponents = async (
  id: string | undefined | null,
): Promise<Array<ConfigComponentArchivedSummary>> =>
  api.get(`pvsim/costing/configs/archived-components?configId=${id}`).json();

export const getArchivedPvSimComponents = async (
  id: string | undefined | null,
): Promise<Array<ConfigComponentArchivedSummary>> =>
  api.get(`pvsim/configs/archived-components?configId=${id}`).json();

export const getPVSimulationLogs = async (): Promise<FlowRunLogs[]> => api.get('pvsim/logs').json();

export async function runPVCostingSimAndSave(
  config: PVCostingConfiguration,
  user: string,
): Promise<ComponentId> {
  return api
    .post('proxy/pv/costing/simulate', {
      json: config,
      headers: { user },
    })
    .json();
}

export async function runPVCostingQuickSim(
  request: PVCostingRequest,
  user: string,
): Promise<PVCostingOutputs> {
  return api
    .post('proxy/pv/costing/simulate', {
      json: request,
      headers: { user },
    })
    .json();
}

export const useQuickSimMutation = (
  user: string,
  opts?: MutationOptions<PVCostingOutputs, HTTPError, PVCostingRequest>,
) =>
  useMutation<PVCostingOutputs, HTTPError, PVCostingRequest>({
    mutationFn: (request) =>
      api
        .post('proxy/pv/costing/simulate', {
          json: request,
          headers: { user },
        })
        .json(),
    ...opts,
  });

export const generatePvBom = async (
  request: RefreshBomParams | undefined,
  user: string,
): Promise<PVBillOfMaterials> =>
  api.post('proxy/pv/costing/bom', { json: request, headers: { user } }).json();

export const uploadCustomLoss = async (form: FormData): Promise<Task> =>
  api.post('pvsim/custom-losses', { body: form }).json();

export const usePrevResultsQuery = (
  projectId: string | undefined | null,
  configId: string | undefined | null,
  opts?: QueryOptions<PrevResultsComparisonQuery>,
) =>
  useQuery({
    queryKey: ['pvcosting-prev-results', projectId, configId],
    queryFn: () =>
      api
        .get<PrevResultsComparisonQuery>('pvsim/costing/results-comparison', {
          searchParams: {
            ...(projectId ? { projectId } : {}),
            ...(configId ? { configId } : {}),
          },
        })
        .json(),
    staleTime: minutesToMilliseconds(5),
    ...opts,
  });

export const usePvsimConfigQuery = (
  id: OptionalCompoundId | undefined,
  opts?: QueryOptions<PVSimConfiguration> & { prefetch?: () => Promise<void> },
) =>
  useQuery({
    queryKey: ['pvsim-config' satisfies `${ConfigType}-config`, ...(id ?? [])],
    queryFn: async () => {
      await opts?.prefetch?.();
      return getPvsimConfiguration(id?.[0], id?.[1]);
    },
    ...opts,
    staleTime: Number.POSITIVE_INFINITY,
    enabled: id?.[0] ? opts?.enabled : false,
  });

const isPvsimConfiguration = (
  config: PVSimConfiguration | undefined,
): config is PVSimConfiguration => Boolean(config);

export const usePvsimConfigsQuery = (ids: Array<OptionalCompoundId>) => {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: ['pvsim-config' satisfies `${ConfigType}-config`, ...id],
      queryFn: () => getPvsimConfiguration(id[0], id[1]),
      staleTime: Number.POSITIVE_INFINITY,
      enabled: Boolean(id[0]),
    })),
    combine: (queries) => ({
      data: queries.map((query) => query.data).filter(isPvsimConfiguration),
      isLoading: queries.some((query) => query.isLoading),
    }),
  });
};

export const usePvConfigQuery = (
  id: OptionalCompoundId | undefined,
  opts?: QueryOptions<PVCostingConfiguration> & {
    prefetch?: () => Promise<void>;
  },
) =>
  useQuery({
    queryKey: ['pv-config' satisfies `${ConfigType}-config`, ...(id ?? [])],
    queryFn: async () => {
      await opts?.prefetch?.();
      return getPvCostingConfiguration(id?.[0], id?.[1]);
    },
    ...opts,
    staleTime: Number.POSITIVE_INFINITY,
    enabled: id?.[0] ? opts?.enabled : false,
  });

export const usePvsimMultipleResultsQuery = (ids: Array<string> | undefined) =>
  useQuery<Array<ScenarioRecord>>({
    queryKey: ['pvsim-results', ...(ids ?? [])],
    queryFn: () =>
      getMultipleResults(ids ?? [], { pv_sim: true, workflow: true, project: true }, 'scenario_id'),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(ids?.length),
  });

type PvCostingResultQuery = Pick<ScenarioRecord, 'project' | 'start_time' | 'workflow'> & {
  pvcosting: PvCostingResult;
  cost_database?: PVCostDatabase;
  hv_cost_database?: HVCostDatabaseV4 | HVCostDatabase;
};

export const usePvCostingScenarioQuery = (id: string | undefined | null, includeDbs = false) =>
  useQuery<PvCostingResultQuery>({
    queryKey: ['pv-costing-results', `includeDbs=${includeDbs}`, id],
    queryFn: () =>
      api
        .get(`pvsim/costing/results/${id}`, { searchParams: { includeDbs } })
        .json<PvCostingResultQuery>(),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(id),
  });

export const usePfDesignMutation = (
  user: string | undefined,
  opts?: MutationOptions<Array<PFDesignResults>, HTTPError, PVSimulationRequest>,
) =>
  useMutation({
    mutationFn: (request) =>
      api.post('proxy/pv/pf-design', { json: request, headers: { user }, timeout: 60e3 }).json(),
    ...opts,
  });

export const useGenerateBom = (
  user: string | undefined,
  opts?: MutationOptions<
    PVBillOfMaterials,
    HTTPError,
    { params: RefreshBomParams; action: 'reset' | 'sync' }
  >,
) =>
  useMutation({
    mutationFn: ({ params }) => generatePvBom(params, user ?? ''),
    ...opts,
  });

export const useAcCableQuantitiesMutation = (
  user: string | undefined,
  opts?: MutationOptions<CableLengths['ac'], HTTPError, AcCableQuantitiesRequest>,
) =>
  useMutation({
    mutationFn: (request) =>
      api.post('proxy/pv/ac-cable-quantities', { json: request, headers: { user } }).json(),
    ...opts,
  });

export const useGenerateBomQuery = (
  params: RefreshBomParams | undefined,
  user: string | undefined,
  opts?: QueryOptions<PVBillOfMaterials>,
) =>
  useQuery({
    queryKey: ['generate-pv-bom', ...objectToQueryKey(params)],
    queryFn: () => generatePvBom(params, user ?? ''),
    ...opts,
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(params) && (opts?.enabled ?? true),
  });

export const usePvsimScenarioQuery = (
  scenarioId: string | undefined | null,
  opts?: QueryOptions<{
    request: PVSimulationRequest;
    workflow: ScenarioWorkflow;
  }> & { prefetch?: () => Promise<void> },
) =>
  useQuery({
    queryKey: ['pvsim-simulate', scenarioId],
    queryFn: async () => {
      await opts?.prefetch?.();
      return getResult<{
        request: PVSimulationRequest;
        workflow: ScenarioWorkflow;
      }>(scenarioId, { 'pv_sim.request': true, workflow: true }, 'scenario_id');
    },
    ...opts,
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(scenarioId) && (opts?.enabled ?? true),
  });

export const useMetsoilRunsForProject = (
  projectId: string | undefined | null,
  opts?: QueryOptions<Array<MetSoilRowWithMetStation>>,
) =>
  useQuery({
    queryKey: ['project-metsoil-runs', projectId],
    queryFn: () => api.get(`metsim/metsoil/${projectId}`).json<Array<MetSoilRowWithMetStation>>(),
    ...opts,
    enabled: Boolean(projectId) && (opts?.enabled ?? true),
    staleTime: minutesToMilliseconds(5),
    meta: { suppressErrorToast: true },
  });

export const useCostingVariablesQuery = (
  request: PVCostingRequest | undefined | null,
  user: string,
  opts?: QueryOptions<CostingVariables, HTTPError>,
) =>
  useQuery({
    queryKey: [
      'costing-variables',
      request?.land.surface_multiplier,
      request?.land.design_acreage,
      request?.land.min_lease_acreage,
      request?.land.fence_perimeter_multiplier,
      request?.pvsim_config.id,
      request?.transmission.miles_to_ic,
      request?.land.perimeter_ft,
      ...objectToQueryKey(request?.cabling?.ac),
    ],
    queryFn: () =>
      api
        .post('proxy/pv/costing/variables', { json: request, headers: { user } })
        .json<CostingVariables>(),
    ...opts,
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(request) && (opts?.enabled ?? true),
  });

export const usePvsimArchivedComponentsQuery = (configId: string | undefined) =>
  useQuery<Array<ConfigComponentArchivedSummary>>({
    queryKey: ['check-archived-components', 'pvsim', configId],
    queryFn: () => getArchivedPvSimComponents(configId),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(configId),
  });
